import styled from '@emotion/styled';
import { motion } from 'framer-motion';

export const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 10001;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 15vh;
`;

export const PaletteContainer = styled(motion.div)`
  width: 90%;
  max-width: 640px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  position: relative;
`;

export const PanelHeader = styled.div<{ colors: any }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 2px solid #e5e7eb;
  background: ${props => props.colors.primary};
`;

export const HeaderTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SearchHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  gap: 12px;
  position: relative;
  
  @media (max-width: 480px) {
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    flex-wrap: nowrap;
  }
`;

export const SearchIcon = styled.div`
  width: 20px;
  height: 20px;
  color: #9ca3af;
  flex-shrink: 0;
  order: 0;
  
  @media (max-width: 480px) {
    order: 0;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  font-family: inherit;
  color: #111827;
  min-width: 0;
  
  &:focus:not(:focus-visible) {
    outline: none !important;
    box-shadow: none !important;
  }
  
  /* Use global focus styles for keyboard navigation */
  &:focus-visible:not(.mouse-focus) {
    /* Let global styles apply - they use türkis (#00d9ff) */
  }
  
  &.mouse-focus {
    outline: none !important;
    box-shadow: none !important;
  }
  
  &.mouse-focus:focus {
    outline: none !important;
    box-shadow: none !important;
  }
  
  &.mouse-focus:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
  
  @media (max-width: 480px) {
    order: 1;
    flex: 1 1 auto;
    min-width: 0;
  }
`;

export const SearchButton = styled.button<{ colors: any }>`
  background: ${props => props.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.colors.primaryHover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    order: 3;
    flex: 0 0 auto;
  }
`;

export const KeyboardHint = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #9ca3af;
  margin-left: auto;
  
  kbd {
    padding: 2px 6px;
    background: #f3f4f6;
    border-radius: 4px;
    border: 1px solid #d1d5db;
    font-family: monospace;
    font-size: 11px;
  }
  
  @media (max-width: 480px) {
    display: flex;
    font-size: 10px;
    
    kbd {
      padding: 2px 4px;
      font-size: 9px;
    }
  }
`;

export const CloseButton = styled.button<{ colors: any }>`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  order: 3;
  
  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 6px;
    order: 3;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

export const ResultsList = styled.div`
  overflow-y: auto;
  max-height: calc(70vh - 70px);
  padding: 8px;
`;

export const ResultItem = styled(motion.div)<{ isActive: boolean; colors: any }>`
  padding: 12px 16px;
  border-radius: 8px;
  background: ${props => props.isActive ? '#f9fafb' : 'transparent'};
  border: 2px solid ${props => props.isActive ? 'transparent' : 'transparent'};
  margin-bottom: 4px;
  transition: all 0.15s ease;
  
  /* Türkiser Rahmen für aktive Ergebnisse - konsistent mit TextToSpeechPlayer */
  ${props => props.isActive && `
    outline: 3px solid #00d9ff;
    outline-offset: 2px;
    box-shadow: 0 0 0 4px rgba(0, 217, 255, 0.3);
  `}

  &:hover {
    background: #f9fafb;
  }
`;

export const ResultTitle = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ResultPath = styled.div<{ colors: any }>`
  font-size: 12px;
  color: ${props => props.colors.primary};
  margin-bottom: 4px;
`;

export const ResultPreview = styled.div<{ colors: any }>`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.4;
  
  mark {
    /* Türkiser Hintergrund für bessere Sichtbarkeit im Popup */
    background: rgba(0, 217, 255, 0.2);
    color: ${props => props.colors.primary};
    padding: 2px 4px;
    border-radius: 2px;
    font-weight: 500;
  }
`;

export const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  
  kbd {
    padding: 4px 8px;
    background: #f3f4f6;
    border-radius: 4px;
    border: 1px solid #d1d5db;
    font-family: monospace;
    font-size: 12px;
    color: #6b7280;
  }
`;

export const NavigationHint = styled.div`
  padding: 12px 20px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #6b7280;
  
  span {
    display: flex;
    align-items: center;
    gap: 4px;
    
    kbd {
      padding: 2px 6px;
      background: white;
      border-radius: 4px;
      border: 1px solid #d1d5db;
      font-family: monospace;
    }
  }
`;

export const AIToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  background: #f3f4f6;
  border: none;
  margin-left: auto;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &:hover {
    background: #e5e7eb;
  }
  
  &:focus-visible {
    outline: 3px solid #00d9ff;
    outline-offset: 2px;
    box-shadow: 0 0 0 3px rgba(0, 217, 255, 0.3), 0 0 0 6px rgba(255, 255, 255, 0.8);
  }
  
  @media (max-width: 480px) {
    margin-left: auto;
    order: 0;
  }
`;

export const ToggleSwitch = styled.div<{ isActive: boolean; colors: any }>`
  width: 36px;
  height: 20px;
  background: ${props => props.isActive ? props.colors.primary : '#9ca3af'};
  border-radius: 10px;
  position: relative;
  transition: all 0.2s ease;
  
  &::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 50%;
    top: 2px;
    left: ${props => props.isActive ? '18px' : '2px'};
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

export const ToggleLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  user-select: none;
`;

export const AIAnswerBox = styled(motion.div)<{ colors: any }>`
  background: ${props => props.colors.bgCard};
  backdrop-filter: blur(5px);
  border: 2px solid ${props => props.colors.primary};
  border-radius: 12px;
  padding: 20px;
  margin: 12px 8px;
  
  .answer-label {
    font-size: 12px;
    font-weight: 600;
    color: ${props => props.colors.primary};
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .answer-text {
    color: #111827;
    line-height: 1.6;
    font-size: 15px;
    
    strong {
      font-weight: 600;
      color: #1f2937;
    }
    
    em {
      font-style: italic;
      color: #4b5563;
    }
    
    ul {
      margin: 8px 0;
      padding-left: 24px;
      
      li {
        margin: 4px 0;
        list-style-type: disc;
      }
    }
  }
`;

export const ResultActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    gap: 6px;
    
    button {
      font-size: 11px;
      padding: 5px 10px;
    }
  }
`;

export const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'tertiary'; colors: any }>`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  white-space: nowrap;
  
  ${props => {
    if (props.variant === 'primary') {
      return `
        background: ${props.colors.primary};
        color: white;
        &:hover { background: ${props.colors.primaryHover}; }
      `;
    } else if (props.variant === 'secondary') {
      return `
        background: #f3f4f6;
        color: #6b7280;
        &:hover { background: #e5e7eb; }
      `;
    } else {
      return `
        background: transparent;
        color: #9ca3af;
        &:hover { color: #6b7280; }
      `;
    }
  }}
  
  @media (max-width: 480px) {
    padding: 5px 10px;
    font-size: 11px;
  }
`;

